import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * Какой раздел медиа открыт в правой колонке: звуки, гифки или музыка.
 *
 * КОНТЕКСТ, А НЕ МЕСТНОЕ СОСТОЯНИЕ: переключатель живёт в верхней полосе, а
 * содержимое - в колонке под ней. Это две разные ветки дерева, и местный хук
 * дал бы им два разных мнения о текущем разделе - ровно тот дефект, который
 * уже случился с режимом раскладки.
 *
 * Выбор ПЕРЕЖИВАЕТ ПЕРЕЗАГРУЗКУ: стример, который весь эфир пользуется
 * гифками, не должен возвращать вкладку после каждого обновления страницы.
 */
export type MediaTab = 'sounds' | 'gif' | 'music';

const KEY = 'efira.console.media.v1';

interface MediaTabs {
  tab: MediaTab;
  setTab: (tab: MediaTab) => void;
}

const MediaTabContext = createContext<MediaTabs>({ tab: 'sounds', setTab: () => undefined });

function stored(): MediaTab {
  try {
    const value = window.localStorage.getItem(KEY);
    return value === 'gif' || value === 'music' ? value : 'sounds';
  } catch {
    // Приватное окно и запрет хранилища - не повод падать: берём звуки.
    return 'sounds';
  }
}

export function MediaTabProvider({ children }: { children: ReactNode }) {
  const [tab, setTabState] = useState<MediaTab>(stored);

  const setTab = useCallback((next: MediaTab) => {
    setTabState(next);

    try {
      window.localStorage.setItem(KEY, next);
    } catch {
      // Выбор всё равно сработал в этой вкладке - молчать здесь можно.
    }
  }, []);

  const value = useMemo(() => ({ tab, setTab }), [tab, setTab]);

  return <MediaTabContext.Provider value={value}>{children}</MediaTabContext.Provider>;
}

export function useMediaTab(): MediaTabs {
  return useContext(MediaTabContext);
}
